/**
 * Clipboard helper for chat artifacts.
 *
 * Dispatches by media type so the user gets the most useful paste target:
 *   - image → image bytes (re-encoded to PNG since ClipboardItem only
 *     reliably accepts ``image/png`` across browsers)
 *   - text-like (markdown / code / html / csv / any ``text/*`` / json / xml)
 *     → fetch body and ``writeText``
 *   - everything else (video, audio, pdf, xlsx, unknown binary) → copy the
 *     file URL so the user can paste it into chat / docs
 */

import { pickPreviewKindForAttachment } from '@/lib/attachment-preview'

export interface CopyArtifactInput {
  url: string
  name: string
  contentType: string | null | undefined
}

export async function copyArtifact(input: CopyArtifactInput): Promise<void> {
  const { url, name, contentType } = input
  const ct = (contentType ?? '').toLowerCase()
  const baseType = ct.split(';')[0].trim()
  const kind = pickPreviewKindForAttachment(name, ct)

  if (baseType.startsWith('image/') || kind === 'image') {
    const blob = await fetchBlob(url)
    const pngBlob =
      blob.type === 'image/png' ? blob : await reencodeToPng(blob)
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': pngBlob }),
    ])
    return
  }

  const isTextLike =
    kind === 'markdown' ||
    kind === 'code' ||
    kind === 'html' ||
    baseType.startsWith('text/') ||
    baseType === 'application/json' ||
    baseType === 'application/xml'
  if (isTextLike) {
    const text = await fetchText(url)
    await navigator.clipboard.writeText(text)
    return
  }

  if (kind === 'spreadsheet') {
    const tsv = await spreadsheetToTsv(url)
    if (tsv != null) {
      await navigator.clipboard.writeText(tsv)
      return
    }
  }

  await navigator.clipboard.writeText(url)
}

async function spreadsheetToTsv(url: string): Promise<string | null> {
  try {
    const blob = await fetchBlob(url)
    const buf = await blob.arrayBuffer()
    const { read, utils } = await import('xlsx')
    const wb = read(buf, { type: 'array' })
    const firstName = wb.SheetNames[0]
    if (!firstName) return null
    return utils.sheet_to_csv(wb.Sheets[firstName], { FS: '\t' })
  } catch (err) {
    console.error('[copy-artifact] spreadsheet → TSV failed', err)
    return null
  }
}

async function fetchBlob(url: string): Promise<Blob> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`fetch failed (${res.status})`)
  return res.blob()
}

async function fetchText(url: string): Promise<string> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`fetch failed (${res.status})`)
  return res.text()
}

function reencodeToPng(blob: Blob): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob)
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(objectUrl)
        reject(new Error('canvas context unavailable'))
        return
      }
      ctx.drawImage(img, 0, 0)
      canvas.toBlob(out => {
        URL.revokeObjectURL(objectUrl)
        if (!out) {
          reject(new Error('canvas toBlob returned null'))
          return
        }
        resolve(out)
      }, 'image/png')
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('image decode failed'))
    }
    img.src = objectUrl
  })
}
